import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import {
  Form,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IssuesService } from 'src/app/_services/issues.service';
import { VehiclesService } from 'src/app/_services/vehicles.service';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Location } from '@angular/common';
import { MessageService } from 'primeng/api';
import { HotkeysService } from 'src/app/_services/hotkeys.service';
import { Hotkey } from 'src/app/_models/hotkey';

@Component({
  selector: 'app-active-issue-form',
  templateUrl: './active-issue-form.component.html',
  styleUrls: ['./active-issue-form.component.scss'],
})
export class ActiveIssueFormComponent implements OnInit {
  activeIssueForm: FormGroup; // Form group for the active issue form
  vehicleList: any[] = []; // List of vehicles
  selectedVehicle: any; // Currently selected vehicle
  signedOff: boolean = false; // Flag to check if signed off
  signOfTypes: any[] = []; // List of sign-off types
  loading: boolean = false; // Flag to check if loading
  suggestions: any[] = [
    { label: 'Issue 1', value: 'Issue 1', new: false },
    { label: 'Issue 2', value: 'Issue 2', new: false },
    { label: 'Issue 3', value: 'Issue 3', new: false },
  ];

  suggestedIssues: any[] = []; // List of suggested issues
  selectedIssues: any[] = []; // List of selected issues
  editFormData: any; // Data for editing an existing issue
  selectedSignOffType: any; // Currently selected sign-off type
  selectedRemarks: any; // Remarks for the issue
  isInEditMode: boolean = false; // Flag to check if in edit mode
  getVehicleReference: any; // Placeholder for vehicle reference
  editFormGroups: FormGroup[] = []; // Form groups for editing multiple issues
  suggestedDescriptions: any[] = []; // List of suggested descriptions
  vehicleReference: any; // Placeholder for vehicle reference

  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private fb: FormBuilder,
    private issuesService: IssuesService,
    private vehiclesService: VehiclesService,
    private router: Router,
    public location: Location,
    public messageService: MessageService,
    public hotkeysService: HotkeysService,
  ) {
    // Initialize the form with default values and validators
    this.activeIssueForm = this.fb.group({
      vehicle_reference: ['', Validators.required],
      description: [[], Validators.required],
      remarks: ['', Validators.required],
      vehicle_id: [''],
      label: '',
      value: '',
    });
  }

  // commentEnabled: boolean;

  vehiclesList: any; // List of vehicles
  selectMapObject = {
    key: 'id',
    descriptorFields: 'vehicle_ID',
  }; // Object to map select options

  ngOnInit() {
    this.isInEditMode = false; // Initially not in edit mode
    // // console.log(this.isInEditMode);

    this.getVehiclesOptionsList(); // Fetch the list of vehicles

    // Define sign-off types
    this.signOfTypes = [
      { signoff_type: 'General' },
      { signoff_type: 'Technical' },
      { signoff_type: 'Fines' },
    ];

    this.updateFormData(); // Update form data if in edit mode
    // // console.log('edit form group', this.editFormGroups);
  }

  // Fetches the list of vehicles from the service
  getVehiclesOptionsList() {
    return this.vehiclesService.getAllVehicles().subscribe((vehicles: any) => {
      this.vehicleList = vehicles.body
        .map((vehicle: any) => ({
          vehicle_reference: vehicle.vehicle_reference,
          vehicle_id: vehicle.id,
        }))
        .sort((a: any, b: any) =>
          a.vehicle_reference.localeCompare(b.vehicle_reference),
        );

      // // console.log('Filtered vehicle list', this.vehicleList);
    });
  }

  onChangeVehicle(event: any) {
    // // console.log('Dropdown change event:', event);

    const selectedVehicle = this.vehicleList.find(
      (vehicle) => vehicle.vehicle_reference === event.value,
    );

    if (selectedVehicle) {
      this.activeIssueForm.patchValue({
        vehicle_reference: selectedVehicle.vehicle_reference,
        vehicle_id: selectedVehicle.vehicle_id,
      });

      // // console.log('Selected vehicle:', this.activeIssueForm.value);
    }
  }

  // Filter descriptions based on the query input
  filterDescriptions(event: any) {
    const query = event.query;

    this.hotkeysService.filterHotkeys(query).subscribe((hotkeys: Hotkey[]) => {
      console.log('hotkeys', hotkeys);

      this.suggestedDescriptions = hotkeys.flatMap(
        (hotkey) =>
          hotkey.descriptions?.map((description) => ({
            label: `${description.hotkey_tag} - ${description.tag_description}`,
            value: `${description.tag_description}`,
          })) || [],
      );

      if (this.suggestedDescriptions.length === 0) {
        this.suggestedDescriptions.push({
          label: `Add "${query}"`,
          value: query,
          new: true,
        });
      }
    });
  }

  onDescriptionSelect(event: any) {
    let selectedIssue = event;
    selectedIssue.label = selectedIssue.value;
    this.selectedIssues.push(selectedIssue);
  }

  // Updates the form data if in edit mode
  updateFormData() {
    this.editFormData = this.config.data.formdata;
    // // console.log('editFormData', this.editFormData);
    if (this.editFormData instanceof Array) {
      if (this.editFormData.length > 0) {
        this.editFormData.forEach((desc: any) => {
          const formGroup = this.fb.group({
            id: desc.id,
            vehicle_reference: [desc.vehicle_reference],
            // Each row backing this form is a single ActiveIssues record,
            // which only ever has one `description` string - the
            // autocomplete above is single-select to match, so the value
            // here is a single {label, value} object, not an array.
            description: [
              {
                label: desc.description,
                value: desc.description,
              },
              Validators.required,
            ],
            remarks: [desc.remarks, Validators.required],
          });
          this.editFormGroups.push(formGroup);
        });
        this.isInEditMode = true;
      }
    } else if (this.editFormData) {
      const transformedData = {
        ...this.editFormData,
        description: Array.isArray(this.editFormData.description)
          ? this.editFormData.description
          : [this.editFormData.description],
      };
      this.activeIssueForm.patchValue(transformedData);
      this.isInEditMode = true;
      this.activeIssueForm.patchValue(this.editFormData!);
    }
  }

  // Implement trackBy function for ngFor trackBy directive to optimize rendering
  trackByFn(index: number, item: any): number {
    return index;
  }

  // Maps data to select options format
  mapDataToOptions(dataList: any): any {
    let newData = dataList.map((data: any) => {
      return {
        label: data[this.selectMapObject.descriptorFields],
        value: data[this.selectMapObject.descriptorFields],
      };
    });

    newData = newData.sort((a: any, b: any) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
    // console.log('new data', newData);
    return newData;
  }

  // Handles sign-off checkbox change event
  onSignOff(event$: any) {
    this.signedOff = event$.checked;
  }

  // Handles issue selection from suggestions
  onIssueSelect($event: any) {
    let selectedIssue = $event;

    if (selectedIssue.new) {
      // Create a new issue if it's a new selection
      selectedIssue = {
        label: selectedIssue.value,
        value: selectedIssue.value,
        new: true,
      };
      this.selectedIssues.push(selectedIssue);
    }
  }

  // Filters issues based on the query input
  filterIssues($event: any) {
    let filtered: any[] = [];
    let query = $event.query;

    this.suggestions.filter((issue) => {
      if (
        String(issue.label)
          .toLocaleLowerCase()
          .includes(query.toLocaleLowerCase())
      ) {
        filtered.push(issue);
      }
    });

    this.suggestedIssues = filtered;

    // Add the option to create a new item if no matches found
    if (this.suggestedIssues.length === 0) {
      this.suggestedIssues.push({
        label: `Add "${query}"`,
        value: query,
        new: true,
      });
    }
  }

  // Handles form submission for creating or updating an issue
  onActiveIssueFormSubmit() {
    this.loading = true;

    for (let i = 0; i < this.editFormGroups.length; i++) {
      if (this.editFormGroups[i].invalid) {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please fill in all the required fields',
          key: 'active_issues',
        });
        return;
      }
    }

    if (!this.editFormData) {
      if (this.activeIssueForm.invalid) {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please fill in all the required fields',
          key: 'active_issues',
        });
        return;
      }

      const formValue = { ...this.activeIssueForm.value };
      formValue.description =
        formValue.description.label || formValue.description;
      //formValue.description = formValue.description.map((desc: any) => desc.label || desc);
      try {
        const formattedIssue =
          this.issuesService.formatSubmittedIssue(formValue);

        this.issuesService.addActiveIssue(formattedIssue).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Issue has been added',
              key: 'active_issues',
            });
            this.loading = false;
            this.ref.close(formattedIssue);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Issue has failed to add',
              key: 'active_issues',
            });
            this.loading = false;
          },
        });
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Issue has failed to add',
          key: 'active_issues',
        });
        this.loading = false;
      }
    } else if (this.editFormData) {
      if (this.editFormGroups.some((formGroup) => formGroup.invalid)) {
        this.editFormGroups.forEach((formGroup) =>
          formGroup.markAllAsTouched(),
        );
        return;
      }

      if (this.ref) {
        this.isInEditMode = false;
        if (this.editFormGroups.length > 0) {
          const updates = this.editFormGroups.map((formGroup) => {
            const formGroupValue = { ...formGroup.value };
            formGroupValue.description =
              formGroupValue.description?.label ?? formGroupValue.description;
            return this.issuesService.updateActiveIssue(
              formGroupValue.id,
              formGroupValue,
            );
          });

          forkJoin(updates).subscribe({
            next: () => {
              this.loading = false;
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `${updates.length} issue(s) successfully updated`,
                key: 'active_issues',
              });
              this.ref.close('1');
            },
            error: () => {
              this.loading = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Issue has failed to update',
                key: 'active_issues',
              });
            },
          });
        } else {
          const formValue = { ...this.activeIssueForm.value };
          formValue.description =
            formValue.description.label || formValue.description;
          try {
            const formattedIssue =
              this.issuesService.formatSubmittedIssue(formValue); // Format the issue
            this.issuesService
              .updateActiveIssue(this.editFormData.id, formattedIssue)
              .subscribe(
                () => {
                  this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Issue has successfully updated',
                    key: 'active_issues',
                  });
                  this.ref.close(formattedIssue);
                },
                (error) => {
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Issue has failed to update',
                    key: 'active_issues',
                  });
                },
              ); // Update the formatted issue
          } catch (error) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Issue has failed to update',
              key: 'active_issues',
            });
          }
        }
      }
    }
  }
}
